/* assets/littleg-family-route-alias.js — FAMILY-ROUTE-CONTEXT-27
 * Owner: documented URL compatibility and canonical Global Sidebar route projection.
 *
 * Spec v2.0 Canonical route keys:
 * - LittleG: place / classroom / map / projects / account
 * - Professional: place / classroom / map / projects / tools / account
 *
 * Canonical destinations:
 * - Place / Search Result: /search
 * - Operational Map: /pages/gproperty-map?owner=map&workspace=map&intent=explore
 * - Projects / Properties workspace: /pages/gproperty-map?owner=projects&workspace=properties&intent=explore
 * - Tools: /pages/gcitypartners?owner=tools&workspace=professional&intent=layers
 * - /place, /map, /projects and /tools are the only retained compatibility aliases.
 * - /search is the canonical LittleG Place Diagnosis route and is never treated as a legacy alias.
 * - /pages/gproject-showcases remains Case Library / proof content, not the Projects workspace destination.
 *
 * Workspace owner rule:
 * - Visual continuity does not imply navigation continuity.
 * - The same map instance may preserve camera / zoom / selected place while the shell owner changes.
 * - Explicit same-document owner changes are projected through littleg:workspace-owner-change.
 * - Local Nav remains owned by the current Page / Workspace; this runtime only projects Global Sidebar state.
 *
 * Boundary:
 * - Sidebar rail/drawer state belongs to assets/littleg-app-sidebar.js.
 * - Sidebar DOM belongs to snippets/littleg-app-sidebar.liquid.
 * - Search/Intent dispatch belongs to assets/main-search.js.
 */
(function () {
  'use strict';

  var VERSION = '2026-10-30-sidebar-route-context-28';
  var GLOBAL_KEY = '__LITTLEG_FAMILY_ROUTE_ALIAS_RUNTIME__';
  var ROOT_SELECTOR = '[data-littleg-app-sidebar]';

  var LITTLEG_ROUTES = Object.freeze(['place', 'classroom', 'map', 'projects', 'account']);
  var PROFESSIONAL_ACTIVE_ROUTES = Object.freeze([
    'place',
    'classroom',
    'map',
    'projects',
    'tools',
    'account',
  ]);

  var PLACE_PAGE_HANDLES = Object.freeze([
    'place',
    'gproperty',
    'surveying',
    'urban-renewal',
    'valuation-advisory',
    'advisory-workflow',
    'why-we-are-building-this',
    'deliverables',
    'credibilities',
    'gcitypartners-registration',
    'smart-village-platform',
  ]);
  var MAP_PAGE_HANDLES = Object.freeze([
    'map',
    'game',
    'gproperty-map',
    'littleg-map',
    'kid-map',
    'spatial-intelligence',
  ]);
  var PROJECT_PAGE_HANDLES = Object.freeze([
    'projects',
    'gproperty-family-fit',
    'gproperty-owner',
    'gproperty-assets',
    'gproperty-report',
    'school-catchment-property',
    'gtender',
    'gtender-dashboard',
    'opportunity-map',
    'gproject-showcases',
    'project-workspace',
    'report-center',
    'result-history',
    'gtender-partner-dashboard',
  ]);
  var CLASSROOM_PAGE_HANDLES = Object.freeze(['classroom', 'api-docs', 'security-whitepaper']);
  var TOOL_PAGE_HANDLES = Object.freeze([
    'gcitypartners',
    'generator',
    'spatial-os',
    'agent-leads',
  ]);
  var ACCOUNT_PAGE_HANDLES = Object.freeze(['littleg-dashboard', 'my-city', 'profile', 'settings']);
  var PROJECT_WORKSPACES = Object.freeze([
    'properties',
    'property',
    'gproperty',
    'gtender',
    'tender',
    'projects',
  ]);
  var TOOL_WORKSPACES = Object.freeze(['tools', 'professional', 'gcitypartners']);
  var GAME_SCREENS = Object.freeze([
    'lobby',
    'brief',
    'photo',
    'guess',
    'reveal',
    'card',
    'share',
    'map',
  ]);
  var LEGACY_QUERY_KEYS = Object.freeze(['estate_name', 'keywords', 'keyword', 'query']);
  /** @type {Set<DirectoryRoot>} */
  const mountedRoots = new Set();

  if (window[GLOBAL_KEY] && typeof window[GLOBAL_KEY].boot === 'function') {
    window[GLOBAL_KEY].boot(document);
    return;
  }

  function toArray(nodes) {
    return Array.prototype.slice.call(nodes || []);
  }
  function clean(value, maxLength) {
    var output = String(value == null ? '' : value).trim();
    return maxLength ? output.slice(0, maxLength) : output;
  }
  function lower(value) {
    return clean(value).toLowerCase();
  }
  function allowedRoutes(root) {
    var experience = lower(root && root.getAttribute('data-sidebar-experience'));
    return experience === 'littleg' ? LITTLEG_ROUTES : PROFESSIONAL_ACTIVE_ROUTES;
  }
  function normalizePath(value) {
    var path = String(value || '/')
      .split('?')[0]
      .split('#')[0]
      .replace(/\\/g, '/')
      .replace(/\/{2,}/g, '/')
      .toLowerCase();
    if (path.charAt(0) !== '/') path = '/' + path;
    if (path.length > 1) path = path.replace(/\/+$/, '');
    return path || '/';
  }
  function roots(scope) {
    var base = scope && scope.querySelectorAll ? scope : document;
    var found = [];
    if (base.matches && base.matches(ROOT_SELECTOR)) found.push(base);
    return found
      .concat(toArray(base.querySelectorAll(ROOT_SELECTOR)))
      .filter(function (root, index, values) {
        return values.indexOf(root) === index;
      });
  }
  function currentUrl() {
    try {
      return new URL(window.location.href);
    } catch (_error) {
      return null;
    }
  }
  function sameOriginUrl(value) {
    var raw = clean(value);
    if (!raw || raw === '#' || raw.charAt(0) === '#') return null;
    try {
      var url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
      return url;
    } catch (_error) {
      return null;
    }
  }
  function isGameScreen(value) {
    return GAME_SCREENS.indexOf(lower(value)) !== -1;
  }
  function localePrefix(root) {
    var localeRoot = normalizePath((root && root.getAttribute('data-sidebar-locale-root')) || '/');
    return localeRoot === '/' ? '' : localeRoot;
  }
  function paths(root) {
    var prefix = localePrefix(root);
    return {
      prefix: prefix,
      place: prefix + '/search',
      map: prefix + '/pages/gproperty-map',
      projects: prefix + '/pages/gproperty-map',
      tools: prefix + '/pages/gcitypartners',
      account: prefix + '/account',
      legacyPlace: prefix + '/place',
      legacyMap: prefix + '/map',
      legacyProjects: prefix + '/projects',
      legacyTools: prefix + '/tools',
      dashboard: prefix + '/pages/littleg-dashboard',
      game: prefix + '/pages/game',
    };
  }
  function pageHandleFromLocalPath(localPath) {
    var match = String(localPath || '').match(/^\/pages\/([^/?#]+)/);
    return match ? lower(match[1]) : '';
  }
  function sanitizeLegacyQuery(url) {
    if (!url) return '';
    var query = clean(url.searchParams.get('q'), 160);
    if (!query) {
      LEGACY_QUERY_KEYS.some(function (key) {
        var candidate = clean(url.searchParams.get(key), 160);
        if (!candidate) return false;
        query = candidate;
        return true;
      });
    }
    if (query) url.searchParams.set('q', query);
    LEGACY_QUERY_KEYS.forEach(function (key) {
      url.searchParams.delete(key);
    });
    return query;
  }

  function canonicalizeUrl(url, root) {
    if (!url || url.origin !== window.location.origin) return url;
    var routePaths = paths(root);
    var path = normalizePath(url.pathname);
    var incomingView = lower(url.searchParams.get('view'));
    var isLegacyRoute =
      [
        routePaths.legacyPlace,
        routePaths.legacyMap,
        routePaths.legacyProjects,
        routePaths.legacyTools,
      ].indexOf(path) !== -1;
    var legacyQuery = isLegacyRoute
      ? sanitizeLegacyQuery(url)
      : clean(url.searchParams.get('q'), 160);

    if (path === routePaths.legacyPlace) {
      url.pathname = routePaths.place;
      if (legacyQuery) url.searchParams.set('q', legacyQuery);
      if (!url.searchParams.get('intent')) url.searchParams.set('intent', 'place_check');
      return url;
    }
    if (path === routePaths.legacyMap) {
      url.pathname = routePaths.map;
      url.searchParams.set('owner', 'map');
      url.searchParams.set('workspace', 'map');
      if (!url.searchParams.get('intent')) url.searchParams.set('intent', 'explore');
      return url;
    }
    if (path === routePaths.legacyProjects) {
      url.pathname = routePaths.projects;
      url.searchParams.set('owner', 'projects');
      url.searchParams.set('workspace', 'properties');
      if (!url.searchParams.get('intent')) url.searchParams.set('intent', 'explore');
      return url;
    }
    if (path === routePaths.legacyTools) {
      url.pathname = routePaths.tools;
      url.searchParams.set('owner', 'tools');
      url.searchParams.set('workspace', 'professional');
      if (!url.searchParams.get('intent')) url.searchParams.set('intent', 'layers');
      return url;
    }
    if (path === routePaths.game) {
      var requestedScreen = lower(url.searchParams.get('screen'));
      if (!isGameScreen(requestedScreen) && isGameScreen(incomingView))
        requestedScreen = incomingView;
      if (!isGameScreen(requestedScreen)) requestedScreen = 'map';
      url.searchParams.set('view', 'game');
      url.searchParams.set('screen', requestedScreen);
      if (!url.searchParams.get('intent')) url.searchParams.set('intent', 'explore');
    }
    return url;
  }

  function defaultRoute(root) {
    var route = lower(root && root.getAttribute('data-sidebar-default-route'));
    return allowedRoutes(root).indexOf(route) !== -1 ? route : 'none';
  }
  function explicitWorkspaceRoute(url, root) {
    if (!url) return '';
    var allowed = allowedRoutes(root);
    var owner = lower(url.searchParams.get('owner') || url.searchParams.get('shell_owner'));
    var workspace = lower(url.searchParams.get('workspace'));
    if (owner && allowed.indexOf(owner) !== -1) return owner;
    if (PROJECT_WORKSPACES.indexOf(workspace) !== -1) return 'projects';
    if (TOOL_WORKSPACES.indexOf(workspace) !== -1 && allowed.indexOf('tools') !== -1)
      return 'tools';
    if (workspace === 'map' || workspace === 'explore') return 'map';
    if (workspace === 'place' || workspace === 'diagnosis') return 'place';
    return '';
  }
  function routeForUrl(url, root) {
    if (!url || url.origin !== window.location.origin) return 'none';
    // Dashboard workspace query values are local views, not Global Sidebar owners.
    if (normalizePath(url.pathname) === localePrefix(root) + '/pages/gcitypartners-dashboard')
      return 'none';
    if (normalizePath(url.pathname) === localePrefix(root) + '/pages/littleg-dashboard')
      return 'account';
    var explicitRoute = explicitWorkspaceRoute(url, root);
    if (explicitRoute) return explicitRoute;
    var routePaths = paths(root);
    var path = normalizePath(url.pathname);
    var localPath = path;
    var body = document.body;
    var surface = lower(body && body.getAttribute('data-surface'));
    var experience = lower(root && root.getAttribute('data-sidebar-experience'));
    if (routePaths.prefix && path.indexOf(routePaths.prefix + '/') === 0)
      localPath = path.slice(routePaths.prefix.length) || '/';
    if (localPath.indexOf('/account') === 0 || surface === 'customer') return 'account';
    if (experience === 'gcitypartners' || experience === 'glg_internal') return defaultRoute(root);
    if (localPath === '/' || localPath === '') return 'none';
    if (path === routePaths.place) return 'place';
    if (path === routePaths.map || path === routePaths.legacyMap || path === routePaths.game)
      return 'map';
    if (path === routePaths.legacyProjects) return 'projects';
    if (path === routePaths.tools || path === routePaths.legacyTools)
      return allowedRoutes(root).indexOf('tools') !== -1 ? 'tools' : 'projects';
    if (path === routePaths.dashboard) return 'account';
    var pageHandle = pageHandleFromLocalPath(localPath);
    if (PLACE_PAGE_HANDLES.indexOf(pageHandle) !== -1) return 'place';
    if (
      CLASSROOM_PAGE_HANDLES.indexOf(pageHandle) !== -1 &&
      allowedRoutes(root).indexOf('classroom') !== -1
    )
      return 'classroom';
    if (MAP_PAGE_HANDLES.indexOf(pageHandle) !== -1) return 'map';
    if (PROJECT_PAGE_HANDLES.indexOf(pageHandle) !== -1) return 'projects';
    if (TOOL_PAGE_HANDLES.indexOf(pageHandle) !== -1)
      return allowedRoutes(root).indexOf('tools') !== -1 ? 'tools' : 'projects';
    if (ACCOUNT_PAGE_HANDLES.indexOf(pageHandle) !== -1) return 'account';
    return defaultRoute(root);
  }

  function routeLabel(root, route) {
    var link = root.querySelector('[data-app-sidebar-link="' + route + '"]');
    if (!link) return 'LittleG';
    return clean(link.getAttribute('data-app-sidebar-label') || link.textContent, 80) || 'LittleG';
  }
  function projectRoute(root, url) {
    var route = routeForUrl(url, root);
    var cvLinks = toArray(root.querySelectorAll('[data-nav00-living-cv-tab]'));
    var cvTab = '';
    if (cvLinks.length && url) {
      var handle = pageHandleFromLocalPath(
        normalizePath(url.pathname).slice(localePrefix(root).length)
      );
      if (handle === 'littleg-map' || handle === 'kid-map') cvTab = 'map';
      else if (handle === 'littleg' || handle === 'littleg-dashboard')
        cvTab = lower(url.searchParams.get('tab')) || 'home';
    }
    cvLinks.forEach(function (link) {
      var active = link.getAttribute('data-nav00-living-cv-tab') === cvTab;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
    var cvActive = cvLinks.some(function (link) {
      return link.getAttribute('aria-current') === 'page';
    });
    var workspace = lower(url && url.searchParams && url.searchParams.get('workspace'));
    root.setAttribute('data-sidebar-desktop-active-route', route);
    root.setAttribute('data-sidebar-mobile-active-route', route);
    root.setAttribute('data-sidebar-active-owner', route);
    if (workspace) root.setAttribute('data-sidebar-active-workspace', workspace);
    else root.removeAttribute('data-sidebar-active-workspace');
    if (document.body) {
      document.body.setAttribute('data-shell-owner-route', route);
      if (workspace) document.body.setAttribute('data-shell-workspace', workspace);
      else document.body.removeAttribute('data-shell-workspace');
    }
    toArray(root.querySelectorAll('[data-app-sidebar-link]')).forEach(function (link) {
      var linkRoute = lower(link.getAttribute('data-app-sidebar-link'));
      var active = !cvActive && route !== 'none' && linkRoute === route;
      if (route === 'none' && link.hasAttribute('data-no-legacy-normalize')) {
        var destination = sameOriginUrl(link.getAttribute('href'));
        active =
          !!destination &&
          !!url &&
          normalizePath(destination.pathname) === normalizePath(url.pathname);
      }
      link.classList.toggle('is-active', active);
      link.removeAttribute('aria-current');
      if (active && link.matches('a[href]')) link.setAttribute('aria-current', 'page');
    });
    toArray(document.querySelectorAll('[data-family-toolbar-route]')).forEach(function (node) {
      node.textContent = routeLabel(root, route);
    });
    root.setAttribute('data-sidebar-hydration', 'ready');
  }
  function normalizeFamilyLinks(root, scope) {
    var base = scope && scope.querySelectorAll ? scope : root;
    toArray(base.querySelectorAll('a[href]')).forEach(function (link) {
      if (link.hasAttribute('data-no-legacy-normalize')) return;
      var url = sameOriginUrl(link.getAttribute('href'));
      if (!url) return;
      canonicalizeUrl(url, root);
      link.setAttribute('href', url.pathname + url.search + url.hash);
    });
  }
  /** @param {DirectoryRoot} root */
  function syncRoot(root) {
    if (!root || root.isConnected === false) return;
    normalizeFamilyLinks(root, root);
    projectRoute(root, currentUrl());
  }
  function bindRoot(root) {
    if (!(root instanceof HTMLElement)) return;
    mountedRoots.add(root);
    root.setAttribute('data-route-context-bound', VERSION);
    syncRoot(root);
  }
  function unbindRoot(root) {
    if (!root || !mountedRoots.has(root)) return;
    mountedRoots.delete(root);
    root.removeAttribute('data-route-context-bound');
  }
  function syncAll() {
    mountedRoots.forEach(syncRoot);
  }
  function normalizeCurrentLocation() {
    var root = mountedRoots.values().next().value;
    var target = currentUrl();
    if (!root || !target) return;
    var before = target.pathname + target.search + target.hash;
    canonicalizeUrl(target, root);
    var next = target.pathname + target.search + target.hash;
    var current = window.location.pathname + window.location.search + window.location.hash;
    if (next !== current) window.location.replace(next);
    else if (before !== next) window.history.replaceState(window.history.state, '', next);
  }
  function setContextParam(url, key, value, maxLength) {
    var normalized = clean(value, maxLength || 180);
    if (normalized) url.searchParams.set(key, normalized);
    else url.searchParams.delete(key);
  }
  function projectWorkspaceOwner(detail) {
    var root = mountedRoots.values().next().value;
    var target = currentUrl();
    if (!root || !target || !detail) return false;
    var owner = lower(detail.owner);
    if (allowedRoutes(root).indexOf(owner) === -1) return false;
    var workspace = lower(detail.workspace);
    var context = detail.context && typeof detail.context === 'object' ? detail.context : {};
    target.searchParams.set('owner', owner);
    if (workspace) target.searchParams.set('workspace', workspace);
    else target.searchParams.delete('workspace');
    setContextParam(target, 'place_id', context.placeId || context.place_id, 160);
    setContextParam(target, 'place_label', context.placeLabel || context.place_label, 180);
    setContextParam(target, 'radius', context.radius, 12);
    setContextParam(target, 'intent', context.intent || detail.intent, 80);
    setContextParam(target, 'bbox', context.bbox, 160);
    setContextParam(
      target,
      'selected_building',
      context.selectedBuilding || context.selected_building,
      160
    );
    var next = target.pathname + target.search + target.hash;
    if (detail.history === 'push') window.history.pushState(window.history.state, '', next);
    else window.history.replaceState(window.history.state, '', next);
    projectRoute(root, target);
    window.dispatchEvent(
      new CustomEvent('littleg:shell-owner-changed', {
        detail: {
          owner: owner,
          workspace: workspace,
          context: context,
          url: next,
          source: clean(detail.source) || 'workspace',
        },
      })
    );
    return true;
  }
  function boot(scope) {
    roots(scope || document).forEach(bindRoot);
    normalizeCurrentLocation();
    syncAll();
  }
  function destroy(scope) {
    roots(scope || document).forEach(unbindRoot);
  }

  document.addEventListener('littleg:workspace-owner-change', function (event) {
    projectWorkspaceOwner(event && event.detail ? event.detail : {});
  });
  window.addEventListener('popstate', syncAll);
  window.addEventListener('hashchange', syncAll);
  window.addEventListener('littleg:sidebar-location-change', syncAll);
  document.addEventListener('shopify:section:load', function (event) {
    boot(event && event.target ? event.target : document);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event && event.target ? event.target : document);
  });

  window[GLOBAL_KEY] = {
    version: VERSION,
    boot: boot,
    sync: syncAll,
    destroy: destroy,
    projectWorkspaceOwner: projectWorkspaceOwner,
  };
  window.__LITTLEG_FAMILY_ROUTE_ALIAS__ = VERSION;
  boot(document);
})();
